/**
 * Обнаружение и обработка ошибок загрузки JS-чанков (AI-76).
 *
 * Vite диспатчит `vite:preloadError` для Chrome, но Safari и Firefox
 * выбрасывают TypeError как unhandled rejection. Эти утилиты покрывают
 * оба сценария.
 */

const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'error loading dynamically imported module',
  'loading chunk',
  'loading css chunk',
] as const;

const STORAGE_KEY = 'chunk-reload-timestamp';
const COOLDOWN_MS = 10_000;

/**
 * Проверяет, является ли ошибка неудачной загрузкой JS-чанка.
 *
 * @example
 * ```ts
 * window.addEventListener('unhandledrejection', (event) => {
 *   if (isChunkLoadError(event.reason)) tryChunkReload();
 * });
 * ```
 */
export const isChunkLoadError = (error: unknown): boolean => {
  const message =
    error instanceof Error ? error.message : String(error ?? '');

  return CHUNK_ERROR_PATTERNS.some((pattern) =>
    message.toLowerCase().includes(pattern),
  );
};

/**
 * Перезагружает страницу для получения актуальных чанков.
 * Защита от бесконечного цикла: максимум 1 reload за 10 секунд.
 *
 * @returns `true` если reload был инициирован, `false` если cooldown активен.
 */
export const tryChunkReload = (): boolean => {
  const lastReload = Number(sessionStorage.getItem(STORAGE_KEY) || '0');
  const now = Date.now();

  if (now - lastReload > COOLDOWN_MS) {
    sessionStorage.setItem(STORAGE_KEY, String(now));
    globalThis.location.reload();
    return true;
  }

  return false;
};
